import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { Col, CardBody, Card } from "reactstrap";
import PermissionAccess from '../../PermissionAccess/Access.js';

const API_BASE_URL = process.env.REACT_APP_APIURL;

const EditBankModal = ({ showModalEditBank, handleToggleModalEditBank, onSuccess, id }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();

  const [data, setData] = useState({
    name: '',
    active: 'Active',
  });

  const [errors, setErrors] = useState({
    name: '',
  });

  useEffect(() => {
    if (id && showModalEditBank) {
      fetchBankDetails();
    }
  }, [id, showModalEditBank]);

  const fetchBankDetails = () => {
    setLoading(true);
    const requestData = {
      functionName: 'GetSingleBank',
      postData: {
        id: id
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/GetSingleBank`, requestData, { headers }).then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else {
        if (response.data.status && response.data.data) {
          setData({
            name: response.data.data.name || '',
            active: response.data.data.active === true ? 'Active' : 'InActive',
          });
        }
      }
    }, (error) => {
      setLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      if (errContent) {
        Alerts.swalErrorAlert(errContent);
      } else {
        Alerts.swalErrorAlert(t("Server Error, Please try again later."));
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target || e;
    let error = '';
  
    if (name === 'name' && !value) {
      error = 'Bank name is required';
    }
    
    setData(prevData => ({ ...prevData, [name]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [name]: error }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    let validationErrors = {};

    if (!data.name) { validationErrors.name = 'Bank name is required'; }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      const requestData = {
        functionName: 'UpdateBank',
        postData: {
          bank_id: id,
          name: data.name,
          active: data.active === 'Active'
        }
      };

      const headers = {
        'Content-Type': 'application/json',
        'token': localStorage.getItem('session-id')
      };

      axios.post(`${API_BASE_URL}/UpdateBank`, requestData, { headers }).then((response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          if (response.data.status === true) {
            resetForm();
            handleToggleModalEditBank();
            onSuccess();
            validationErrors.name = '';
            Alerts.swalSuccessAlert(t("Bank updated successfully."));
          } else {
            // Handle specific error messages
            if (response.data.message) {
              if (response.data.message === "A bank with this name already exists") {
                Alerts.swalErrorAlert(t("A bank with this name already exists"));
              } else {
                Alerts.swalErrorAlert(response.data.message);
              }
            } else {
              validationErrors.name = response.data.errors?.[0]?.message || 'An error occurred';
              setErrors(validationErrors);
            }
          }
        }
      }, (error) => {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        if (errContent) {
          // Check if it's an object with a message property
          if (typeof errContent === 'object' && errContent.message) {
            Alerts.swalErrorAlert(errContent.message);
          } else if (typeof errContent === 'string') {
            Alerts.swalErrorAlert(errContent);
          } else {
            Alerts.swalErrorAlert(t("Server Error, Please try again later."));
          }
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      });
    }
  };

  const resetForm = () => {
    setData({
      name: '',
      active: 'Active',
    });
  };

  const handleClose = () => {
    setErrors({
        name: '',
    });
    handleToggleModalEditBank();
  };

  return (
    <div className={showModalEditBank ? "modal d-block mt-4" : "modal"} style={{ display: showModalEditBank ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} tabIndex="-1" role="dialog">
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{t("Edit Bank")}</h5>
            </div>
            <div className="modal-body">
            <Col md="12">
                <Card className="main-card mb-3">
                     <CardBody>
              <LoadingOverlay
                tag="div"
                active={loading}
                styles={{ overlay: (base) => ({ ...base }) }}
                spinner={<DNALoader />}
              >
                <input type="hidden" className="form-control" name="business_id" />

                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label htmlFor="bank-name" className="col-form-label">{t("Bank Name")}:</label>
                      <input type="text" className="form-control" placeholder="Enter Bank Name" 
                      name="name"
                      onChange={handleChange}
                      value={data.name}
                      />
                      {errors.name && <strong className="error text-danger">{errors.name}</strong>}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label htmlFor="bank-status" className="col-form-label">{t("Status")}:</label>
                      <select 
                        className="form-control" 
                        name="active"
                        onChange={handleChange}
                        value={data.active}
                      >
                        <option value="Active">{t("Active")}</option>
                        <option value="InActive">{t("InActive")}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="text-center mt-4">
                  <button type="button" className="btn btn-outline-light btn-hover-shine me-3" onClick={handleClose}>{t("Close")}</button>
                  <button type="submit" className="btn btn-custom-color">{t("Update")}</button>
                </div>
                </div>
                                          </LoadingOverlay>
                                        </CardBody>
                                    </Card>
                                </Col>
            </div>
            
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditBankModal;
